import { Typography } from "@connected-repo/ui-mui/data-display/Typography";
import { Button } from "@connected-repo/ui-mui/form/Button";
import { Box } from "@connected-repo/ui-mui/layout/Box";
import { useStrings } from "@frontend/strings/useStrings";
import { probeServer, useOnlineStatus } from "@frontend/utils/online_status";
import WifiOffRoundedIcon from "@mui/icons-material/WifiOffRounded";
import { keyframes } from "@mui/material/styles";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

const pulse = keyframes`
	0%, 100% { transform: scale(1); opacity: 1; }
	50% { transform: scale(1.6); opacity: 0.35; }
`;

/**
 * Bold, full-width bar pinned to the top of every screen (sign-in included)
 * while the server cannot be reached. Its height is published as the CSS
 * variable `--offline-bar` so the sidebar, top bar and page content move down
 * instead of being covered. Retry probes the server right away.
 */
export const OfflineBanner = () => {
	const { t } = useStrings();
	const online = useOnlineStatus();
	const barRef = useRef<HTMLDivElement>(null);
	const [checking, setChecking] = useState(false);
	const wasOffline = useRef(!online);

	useLayoutEffect(() => {
		const root = document.documentElement;
		const bar = barRef.current;
		if (online || !bar) {
			root.style.setProperty("--offline-bar", "0px");
			return;
		}
		const update = () => root.style.setProperty("--offline-bar", `${bar.offsetHeight}px`);
		update();
		const observer = new ResizeObserver(update);
		observer.observe(bar);
		return () => observer.disconnect();
	}, [online]);

	useEffect(() => {
		if (online && wasOffline.current) toast.success(t.offline.backOnline, { autoClose: 2500 });
		wasOffline.current = !online;
	}, [online, t]);

	if (online) return null;

	return (
		<Box
			ref={barRef}
			role="alert"
			aria-live="assertive"
			data-testid="offline-banner"
			sx={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				zIndex: (theme) => theme.zIndex.modal + 1,
				display: "flex",
				alignItems: "center",
				gap: 1.5,
				px: { xs: 2, md: 3 },
				py: 1.25,
				pt: "calc(10px + env(safe-area-inset-top))",
				bgcolor: "#0B0B0F",
				color: "#fff",
				boxShadow: "0 10px 30px -12px rgba(0,0,0,0.6)",
			}}
		>
			<Box sx={{ position: "relative", display: "grid", placeItems: "center", flexShrink: 0 }}>
				<Box
					sx={{
						position: "absolute",
						width: 10,
						height: 10,
						top: -2,
						right: -3,
						borderRadius: "50%",
						bgcolor: "error.main",
						animation: `${pulse} 1.4s ease-in-out infinite`,
					}}
				/>
				<WifiOffRoundedIcon />
			</Box>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography sx={{ fontWeight: 800, lineHeight: 1.2, color: "inherit" }}>{t.offline.title}</Typography>
				<Typography variant="body2" sx={{ color: "rgba(255,255,255,0.72)", fontWeight: 500, lineHeight: 1.3 }}>
					{t.offline.body}
				</Typography>
			</Box>
			<Button
				size="small"
				variant="outlined"
				disabled={checking}
				onClick={async () => {
					setChecking(true);
					await probeServer();
					setChecking(false);
				}}
				sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)", flexShrink: 0, "&:hover": { borderColor: "#fff" } }}
			>
				{checking ? t.offline.checking : t.offline.retry}
			</Button>
		</Box>
	);
};
